import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { SummaryPanel } from './SummaryPanel';
import type { TileFilter } from './SummaryPanel';
import { UnitList } from './UnitList';
import type { View } from './UnitList';

/** Release switch of the KPI tiles that open their tab of the list. */
const TILE_LINKS = 'inventory-tile-links';

/** Release switch of the reference's tab strip above the dashboard (walk of 2026-10-23). */
const TAB_STRIP = 'inventory-tab-strip';

/** Release switch of View Full Dashboard. */
const FULL_DASHBOARD = 'inventory-full-dashboard';

/** Inventory Dashboard, Add Single / Bulk Unit, Update Single / Bulk Unit, as the reference. */
function InventoryTabs() {
  const { can } = useAuth();
  const tabs: Array<[label: string, to: string, code: string]> = [
    ['Inventory Dashboard', '/inventory', 'PC345'],
    ['Add Single Unit', '/inventory/units/new', 'PC345'],
    ['Add Bulk Unit', '/inventory/bulk/add-units', 'PC345'],
    ['Update Single Unit', '/inventory/update?feature=sim&upload=single', 'PC391'],
    ['Update Bulk Unit', '/inventory/update?feature=sim&upload=bulk', 'PC391'],
  ];
  return (
    <Tabs
      value="/inventory"
      aria-label="Manage Inventory"
      variant="scrollable"
      sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
    >
      {tabs
        .filter(([, , code]) => can(code))
        .map(([label, to]) => (
          <Tab key={label} label={label} value={to} component={Link} to={to} />
        ))}
    </Tabs>
  );
}

/**
 * Manage Inventory (PC345): the "Inventory Dashboard" with KPI tiles, charts and the unit list.
 * The summary and the list load, fail and retry independently of each other.
 */
export function InventoryDashboardPage() {
  const featureOn = useFeatureReleased();
  const [view, setView] = useState<View>('sim');
  // A tile starts the list afresh in its tab, as switching tabs does.
  const [opened, setOpened] = useState(0);
  const [filter, setFilter] = useState<TileFilter | undefined>();
  const listRef = useRef<HTMLDivElement>(null);
  const open = (target: 'sim' | 'device', tileFilter?: TileFilter) => {
    setView(target);
    // With View Full Dashboard released, a tile also filters the list, as the reference does.
    setFilter(featureOn(FULL_DASHBOARD) ? tileFilter : undefined);
    setOpened((n) => n + 1);
    listRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  return (
    <Box>
      <PageHeader
        title="Inventory Dashboard"
        code="PC345"
        actions={
          <>
            {featureOn(FULL_DASHBOARD) ? (
              <Button component={Link} to="/inventory/full" variant="outlined">
                View Full Dashboard
              </Button>
            ) : null}
            <Button component={Link} to="/inventory/manage">
              Switch to Old View
            </Button>
            <Button
              component={Link}
              to="/inventory/units/new"
              variant="contained"
              startIcon={<AddIcon />}
            >
              Add Single Unit
            </Button>
            <Button
              component={Link}
              to="/inventory/bulk/add-units"
              variant="outlined"
              startIcon={<UploadFileIcon />}
            >
              Add Bulk Unit
            </Button>
          </>
        }
      />
      {featureOn(TAB_STRIP) ? <InventoryTabs /> : null}
      <SummaryPanel onOpen={featureOn(TILE_LINKS) ? open : undefined} />
      <Box ref={listRef}>
        <UnitList key={opened} view={view} onViewChange={setView} filter={filter} />
      </Box>
    </Box>
  );
}
